import React, { useState } from 'react';
import ExcelJS from 'exceljs';
import { useSelector } from 'react-redux';
import './ExportExcelModalContent.css';
import ExportProgressOverlay from '../../shared/ExportProgressOverlay';
import { FamilyMapping } from '../../../../utilities/constants/familyConstants';
import { getOrderedHeaders } from '../../../../utilities/constants/excelColumnOrder';
import { resolveInspectorForNode } from '../../../../utilities/inspectors/registry';

const normalizeReportHeader = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .split(' ')
  .filter((word) => !['de', 'del', 'la', 'las', 'el', 'los'].includes(word))
  .join(' ');

const getHeaderDisplayScore = (header) => {
  const words = String(header || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 0;
  return words.reduce((score, word) => {
    if (/^[A-ZÁÉÍÓÚÜÑ0-9]+$/.test(word) && word.length > 1) return score + 2;
    if (/^[A-ZÁÉÍÓÚÜÑ]/.test(word)) return score + 1;
    if (/^[a-záéíóúüñ]/.test(word)) return score - 1;
    return score;
  }, 0);
};

const choosePreferredHeader = (currentHeader, nextHeader) => {
  if (!currentHeader) return nextHeader;
  if (!nextHeader) return currentHeader;
  const currentScore = getHeaderDisplayScore(currentHeader);
  const nextScore = getHeaderDisplayScore(nextHeader);
  if (nextScore !== currentScore) {
    return nextScore > currentScore ? nextHeader : currentHeader;
  }
  return String(nextHeader).length > String(currentHeader).length ? nextHeader : currentHeader;
};

const mergeEquivalentHeaders = (headers) => {
  const seen = new Map();
  headers.forEach((header) => {
    const normalized = normalizeReportHeader(header);
    if (!normalized) return;
    seen.set(normalized, choosePreferredHeader(seen.get(normalized), header));
  });
  return Array.from(seen.values());
};

const getReportValue = (data, header) => {
  if (!data || typeof data !== 'object') return '';
  if (Object.hasOwn(data, header)) return data[header] || '';
  const normalizedHeader = normalizeReportHeader(header);
  const matchingKey = Object.keys(data).find((key) => normalizeReportHeader(key) === normalizedHeader);
  return matchingKey ? (data[matchingKey] || '') : '';
};

function resolveReportLabel(attrKey, attr, canonicalLabelsByKey, resolvedAttributes = {}) {
  const canonicalLabel = canonicalLabelsByKey[attrKey] || attrKey;
  const candidates = [
    attr?.label,
    resolvedAttributes?.[attrKey]?.label,
  ];
  const equivalentLabel = candidates.find((label) => (
    typeof label === 'string' &&
    normalizeReportHeader(label) &&
    normalizeReportHeader(label) === normalizeReportHeader(canonicalLabel)
  ));
  const customLabel = candidates.find((label) => {
    if (typeof label !== 'string') return false;
    const normalized = normalizeReportHeader(label);
    if (!normalized) return false;
    return normalized !== normalizeReportHeader(canonicalLabel);
  });
  return customLabel || choosePreferredHeader(canonicalLabel, equivalentLabel) || canonicalLabel;
}

function getNodeAttributesData(node, labelOverrides = {}) {
  const nodeData = node?.data || {};
  const { attributes } = nodeData;
  if (!attributes) return {}

  const inspectorInfo = resolveInspectorForNode(nodeData, labelOverrides);
  const inspectorFields = Array.isArray(inspectorInfo?.definition?.fields)
    ? inspectorInfo.definition.fields.filter((field) => field?.type !== 'section' && field?.key)
    : [];
  const resolvedAttributes = inspectorInfo?.attributes || {};
  const canonicalLabelsByKey = inspectorFields.reduce((acc, field) => {
    acc[field.key] = field.label || field.key;
    return acc;
  }, {});
  canonicalLabelsByKey.nombre = canonicalLabelsByKey.nombre || 'Nombre';

  const serializeValue = (value) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (Array.isArray(value)) {
      return value.length ? JSON.stringify(value) : '';
    }
    if (typeof value === 'object') {
      return Object.keys(value).length ? JSON.stringify(value) : '';
    }
    return value;
  };

  const shouldIncludeAttribute = (attrKey, attr) => {
    if (!attr) return false;
    if (attrKey === 'nombre') return true;
    if (canonicalLabelsByKey[attrKey]) return true;
    if (attr.systemModified || attr.systemManaged || attr.systemDefined) return false;
    return true;
  };

  const orderedKeys = [
    ...inspectorFields.map((field) => field.key),
    ...Object.keys(attributes),
  ].filter((key, index, list) => key && list.indexOf(key) === index);

  const dataObject = {};
  const resolvedHeaders = {};
  orderedKeys.forEach((attrKey) => {
    if (!Object.hasOwn(attributes, attrKey)) return;
    const attr = attributes[attrKey];
    if (!shouldIncludeAttribute(attrKey, attr)) return;
    const labelOrKey = resolveReportLabel(attrKey, attr, canonicalLabelsByKey, resolvedAttributes);
    const headerKey = normalizeReportHeader(labelOrKey);
    if (!headerKey) return;
    const previousHeader = resolvedHeaders[headerKey];
    const resolvedHeader = choosePreferredHeader(previousHeader, labelOrKey);
    if (previousHeader && previousHeader !== resolvedHeader && dataObject[resolvedHeader] === undefined) {
      dataObject[resolvedHeader] = dataObject[previousHeader];
      delete dataObject[previousHeader];
    }
    resolvedHeaders[headerKey] = resolvedHeader;
    const nextValue = serializeValue(attr.value);
    if (dataObject[resolvedHeader] === undefined || dataObject[resolvedHeader] === '') {
      dataObject[resolvedHeader] = nextValue;
    }
  });
  return dataObject;
}

const EXCLUDED_REPORT_SYMBOLS = new Set(['Box', 'TextBox']);
const DASHBOARD_GROUP = 'dashboard';
const DASHBOARD_LABEL = 'General';

function isReportNode(node) {
  const symbolLabel = node?.data?.symbolLabel;
  if (!symbolLabel || EXCLUDED_REPORT_SYMBOLS.has(symbolLabel)) {
    return false;
  }
  return Object.keys(getNodeAttributesData(node)).length > 0;
}

function getSafeWorksheetName(groupName, usedNames) {
  const fallbackName = 'Reporte';
  const cleanedName = String(groupName || fallbackName)
    .replace(/[\\/?*:[\]]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || fallbackName;
  const baseName = cleanedName.slice(0, 31);
  let worksheetName = baseName;
  let index = 2;

  while (usedNames.has(worksheetName)) {
    const suffix = ` ${index}`;
    worksheetName = `${baseName.slice(0, 31 - suffix.length)}${suffix}`;
    index += 1;
  }

  usedNames.add(worksheetName);
  return worksheetName;
}

function ExportExcelModalContent({ onClose }) {
  // (You could also pass in "nodes" as a prop from modalProps,
  //  instead of reading from the store here.)
  const { nodes } = useSelector((state) => state.node)
  const symbolLabelOverrides = useSelector((state) => state.view?.symbolLabelOverrides || {});
  const hasReportData = React.useCallback(
    (node) => Object.keys(getNodeAttributesData(node, symbolLabelOverrides)).length > 0,
    [symbolLabelOverrides]
  );
  const reportNodes = React.useMemo(
    () => (Array.isArray(nodes) ? nodes.filter((node) => isReportNode(node) && hasReportData(node)) : []),
    [hasReportData, nodes]
  );
  const [exportError, setExportError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('running');
  const selectedDiagramId = useSelector((state) => state.diagram?.selectedId);
  const diagrams = useSelector((state) => state.diagram?.entities || {});
  const diagramInstance = useSelector((state) => state.diagram?.instance);
  const diagramTitle = (() => {
    if (selectedDiagramId && diagrams[selectedDiagramId]) {
      return diagrams[selectedDiagramId].label || diagrams[selectedDiagramId].name || diagrams[selectedDiagramId].title;
    }
    if (diagramInstance) {
      return diagramInstance.label || diagramInstance.name || diagramInstance.title;
    }
    return 'Proyecto';
  })();

  const getMappedGroup = (node) => {
    const rawLabel = node?.data?.symbolLabel || 'Unknown';
    // Use the mapped group if available; otherwise, fall back to the raw label.
    return FamilyMapping[rawLabel] || rawLabel;
  };

  // If you’d like to show checkboxes for *all* labels:
  const allLabels = React.useMemo(() => {
    if (!reportNodes.length) return [];
    const uniqueGroups = Array.from(
      new Set(reportNodes.map((node) => getMappedGroup(node)))
    );
    // Map to objects with value and display (if you need to do further customization)
    return uniqueGroups
    .map((group) => ({
      value: group,
      display: group,
    }))    
    .sort((a, b) => a.display.localeCompare(b.display));
  }, [reportNodes]);

  const [selectedLabels, setSelectedLabels] = useState(() => {
    if (!Array.isArray(nodes) || nodes.length === 0) return [];
    return Array.from(
      new Set(nodes.filter((node) => isReportNode(node) && hasReportData(node)).map((node) => getMappedGroup(node)))
    );
  });

  const handleCheckboxChange = (groupName) => {
    setSelectedLabels((prev) =>
      prev.includes(groupName)
        ? prev.filter((item) => item !== groupName)
        : [...prev, groupName]
    );
  };

  const handleExport = async () => {
    if (!selectedLabels.length) {
      setExportError('Selecciona al menos una familia con datos para exportar.');
      return
    }

    const groupedNodes = reportNodes.reduce((acc, node) => {
      const group = getMappedGroup(node);
      acc[group] = acc[group] || [];
      acc[group].push(node);
      return acc;
    }, {});

    const selectedGroups = selectedLabels
      .map((groupName) => {
        const isDashboard = groupName === DASHBOARD_GROUP;
        return {
          groupName,
          displayName: isDashboard ? DASHBOARD_LABEL : groupName,
          nodesForGroup: isDashboard ? reportNodes : (groupedNodes[groupName] || []),
          includeFamilyColumn: isDashboard,
        };
      })
      .filter(({ nodesForGroup }) => nodesForGroup.length > 0)
      .sort((a, b) => {
        if (a.groupName === DASHBOARD_GROUP) return -1;
        if (b.groupName === DASHBOARD_GROUP) return 1;
        return 0;
      });

    if (!selectedGroups.length) {
      setExportError('No hay datos de reporte para exportar en las familias seleccionadas.');
      return;
    }

    setExportError('');
    setExporting(true);
    setExportStatus('running');
    setProgress(5);
    const progressInterval = setInterval(() => {
      setProgress((value) => (value < 90 ? value + 10 : value));
    }, 300);
    const workbook = new ExcelJS.Workbook()
    const usedWorksheetNames = new Set();

    selectedGroups.forEach(({ groupName, displayName, nodesForGroup, includeFamilyColumn }) => {
      const worksheet = workbook.addWorksheet(getSafeWorksheetName(displayName, usedWorksheetNames));

      // Create a header row using the project title and group name.
      const headerTitle = `${diagramTitle} - ${displayName}`;
      const worksheetHeaderCell = worksheet.getCell('A1');
      worksheetHeaderCell.value = headerTitle;
      worksheetHeaderCell.font = { size: 12, bold: true };
      worksheetHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 25;

      // Collect header columns based on the attributes.
      const headerRow = getOrderedHeaders(
        mergeEquivalentHeaders(
          nodesForGroup.flatMap((node) => {
            const keys = Object.keys(getNodeAttributesData(node, symbolLabelOverrides));
            return includeFamilyColumn ? ['Familia', ...keys] : keys;
          })
        ),
        groupName
      );
      worksheet.addRow(headerRow);
      worksheet.getRow(2).eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ffffff' },
        };
        cell.border = {
          top: { style: 'medium' },
          bottom: { style: 'medium' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
      });

      // Add each node’s data as a row.
      nodesForGroup.forEach((node) => {
        const attributesData = {
          ...(includeFamilyColumn ? { Familia: getMappedGroup(node) } : {}),
          ...getNodeAttributesData(node, symbolLabelOverrides),
        };
        const row = headerRow.map((header) => getReportValue(attributesData, header));
        worksheet.addRow(row);
      });

      // Auto-size the columns.
      worksheet.columns.forEach((column) => {
        const maxLength = column.values.reduce((max, curr) => {
          if (curr && curr.toString().length > max) {
            return curr.toString().length;
          }
          return max;
        }, 10);
        column.width = maxLength + 2;
      });
    });

    // Export the workbook.
    try {
      setProgress(85);
      const buffer = await workbook.xlsx.writeBuffer();
      setProgress(95);
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const sanitizedTitle = (diagramTitle || 'Proyecto').replace(/[\\/:*?"<>|]/g, '_');
      link.download = `${sanitizedTitle || 'reporte'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setProgress(100);
      setExportStatus('success');
      setTimeout(onClose, 500);
    } catch (error) {
      console.error('[ExportExcelModalContent] Export failed', error);
      setExportStatus('error');
      setExportError(error?.message || 'No se pudo generar el archivo de Excel. Intenta de nuevo.');
    } finally {
      clearInterval(progressInterval);
    }
  }

  return (
    <div className="excel-modal__content">
      {exporting ? (
        <ExportProgressOverlay
          progress={progress}
          status={exportStatus}
          error={exportError}
          message="Exportando Excel"
          onClose={onClose}
        />
      ) : null}
      <h2 className="excel-modal__title">Exportar a Excel</h2>
      <h3 className="excel-modal__subtitle">
        Elige las familias que te gustaría agregar en el reporte de excel.
      </h3>

      <div className="excel-modal__checkbox-group">
        {reportNodes.length ? (
          <label key="dashboard-excel" className="excel-modal__checkbox-label">
            <input
              type="checkbox"
              checked={selectedLabels.includes(DASHBOARD_GROUP)}
              onChange={() => handleCheckboxChange(DASHBOARD_GROUP)}
              className="excel-modal__checkbox-input"
            />
            {DASHBOARD_LABEL}
          </label>
        ) : null}
        {allLabels.map((item) => (
          <label key={item.value} className="excel-modal__checkbox-label">
            <input
              type="checkbox"
              checked={selectedLabels.includes(item.value)}
              onChange={() => handleCheckboxChange(item.value)}
              className="excel-modal__checkbox-input"
            />
            {item.display}
          </label>
        ))}
        {!allLabels.length ? (
          <p className="excel-modal__empty">No hay símbolos con datos de reporte para exportar.</p>
        ) : null}
      </div>
      {exportError ? (
        <div className="excel-modal__error" role="alert">
          {exportError}
        </div>
      ) : null}
    

      <div className="excel-modal__actions">
        <button
          onClick={handleExport}
          className="excel-modal__button excel-modal__button--export"
          disabled={exporting && exportStatus !== 'error'}
        >
          Exportar
        </button>
        <button
          onClick={onClose}
          className="excel-modal__button excel-modal__button--cancel"
          disabled={exporting && exportStatus !== 'error'}
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}

export default ExportExcelModalContent
